import { useState, useEffect } from "react";
import { useApproveRequisition, useRejectRequisition } from "../../hooks/useRequisition";
import { useNotification } from "@/shared/hooks/useNotification";
import { useUnitsOfMeasure } from "@/shared/hooks/useUnitsOfMeasure";
import type { Requisition } from "../../services/requisitionApi";
import { getStatusLabel, type ApproveRequisitionItem } from "../../services/requisitionApi";
import { inputsApi, type Input } from "../../services/inputsApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input as TextInput } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CheckCircle2, XCircle, Package, CalendarDays, User, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import {
  getRequisitionItemDescription,
  getRequisitionItemName,
  getRequisitionItemOwner,
  getRequisitionItemReference,
  getRequisitionItemType,
  isCustomerSeedItem,
} from "./requisitionDisplay";

interface RequisitionApprovalDialogProps {
  requisition: Requisition | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const RequisitionApprovalDialog = ({
  requisition,
  open,
  onOpenChange,
  onSuccess,
}: RequisitionApprovalDialogProps) => {
  const [approvalNotes, setApprovalNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [inputsMap, setInputsMap] = useState<Record<string, Input>>({});
  const [loadingInputs, setLoadingInputs] = useState(false);
  const [approvedQuantities, setApprovedQuantities] = useState<Record<string, number>>({});
  // Confirm-before-submit gate. Set to "approve" or "reject" to open the
  // confirmation dialog; the perform* function fires only on confirmation.
  // Declared above the early-return so the useEffect below can reference its
  // setter even when the dialog renders without a requisition (initial mount).
  const [pendingAction, setPendingAction] = useState<"approve" | "reject" | null>(null);
  const { data: unitsOfMeasure = [] } = useUnitsOfMeasure();
  const approveMutation = useApproveRequisition();
  const rejectMutation = useRejectRequisition();
  const notify = useNotification();

  const unitsMap = unitsOfMeasure.reduce(
    (acc, unit) => {
      acc[unit.name.toLowerCase()] = unit.value;
      acc[unit.abbreviation.toLowerCase()] = unit.value;
      return acc;
    },
    {} as Record<string, number>
  );

  useEffect(() => {
    if (!open) {
      setApprovalNotes("");
      setRejectionReason("");
      setShowRejectForm(false);
      setInputsMap({});
      setApprovedQuantities({});
      setPendingAction(null);
      return;
    }

    if (!requisition?.items || requisition.items.length === 0) return;

    const fetchInputs = async () => {
      setLoadingInputs(true);
      try {
        const inputIds = [...new Set(requisition.items.map((item) => item.inputId).filter(Boolean))] as string[];
        const inputs = await Promise.all(
          inputIds.map((id) => inputsApi.getInputById(id))
        );
        const map = inputs.reduce((acc, input) => {
          acc[input.id] = input;
          return acc;
        }, {} as Record<string, Input>);
        setInputsMap(map);

        const quantities: Record<string, number> = {};
        requisition.items.forEach((item, idx) => {
          quantities[idx] = item.quantityRequested;
        });
        setApprovedQuantities(quantities);
      } catch (error) {
        console.error("Failed to fetch input details:", error);
        notify.error("Failed to load requisition item details");
      } finally {
        setLoadingInputs(false);
      }
    };

    fetchInputs();
  }, [open, requisition, notify]);

  if (!requisition) return null;

  const isPending = approveMutation.isPending || rejectMutation.isPending;

  const performApprove = () => {
    const approveItems: ApproveRequisitionItem[] = requisition.items.map((item, idx) => {
      const unitValue = unitsMap[item.unitOfMeasure.toLowerCase()] ?? 0;
      return {
        itemId: item.id || item.inputId || item.seedBatchId || "",
        quantityApproved: approvedQuantities[idx] ?? 0,
        unitOfMeasure: unitValue,
      };
    });

    approveMutation.mutate(
      {
        requisitionId: requisition.id,
        approvalNotes,
        items: approveItems,
      },
      {
        onSuccess: () => {
          notify.success("Requisition approved successfully!");
          onOpenChange(false);
          onSuccess();
        },
        onError: (error: any) => {
          notify.error(error.response?.data?.message || "Failed to approve requisition");
        },
      }
    );
  };

  const performReject = () => {
    if (!rejectionReason.trim()) {
      notify.error("Please provide a reason for rejection");
      return;
    }

    rejectMutation.mutate(
      { id: requisition.id, reason: rejectionReason },
      {
        onSuccess: () => {
          notify.success("Requisition rejected successfully!");
          onOpenChange(false);
          onSuccess();
        },
        onError: (error: any) => {
          notify.error(error.response?.data?.message || "Failed to reject requisition");
        },
      }
    );
  };

  const handleApprove = () => {
    if (!requisition) return;
    setPendingAction("approve");
  };

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      notify.error("Please provide a reason for rejection");
      return;
    }
    setPendingAction("reject");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            Review Requisition
          </DialogTitle>
          <DialogDescription>
            Review the items below and set approved quantities before approving or rejecting.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Summary cards */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
            <div className="rounded-lg border bg-muted/40 p-3 space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <User className="h-3 w-3" /> Department
              </p>
              <p className="text-sm font-medium truncate">{requisition.departmentName}</p>
            </div>
            <div className="rounded-lg border bg-muted/40 p-3 space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Package className="h-3 w-3" /> Items
              </p>
              <p className="text-sm font-medium">{requisition.items.length} item{requisition.items.length !== 1 ? "s" : ""}</p>
            </div>
            <div className="rounded-lg border bg-muted/40 p-3 space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <CalendarDays className="h-3 w-3" /> Required By
              </p>
              <p className="text-sm font-medium">{new Date(requisition.requiredByDate).toLocaleDateString()}</p>
            </div>
            <div className="rounded-lg border bg-muted/40 p-3 space-y-1 min-w-0">
              <p className="text-xs text-muted-foreground">Status</p>
              <Badge variant="outline" className="text-xs truncate max-w-full">
                {getStatusLabel(requisition.status)}
              </Badge>
            </div>
          </div>

          {/* Purpose */}
          <div className="rounded-lg bg-muted/40 border px-4 py-3">
            <p className="text-xs text-muted-foreground mb-1">Purpose</p>
            <p className="text-sm">{requisition.purpose}</p>
          </div>

          <Separator />

          {/* Items Table */}
          <div>
            <p className="text-sm font-medium mb-3">Requisition Items</p>
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Type</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="text-right">Requested</TableHead>
                    <TableHead className="text-right w-36">Approve Qty</TableHead>
                    <TableHead>Unit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingInputs ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                        Loading items…
                      </TableCell>
                    </TableRow>
                  ) : requisition.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                        No items in this requisition
                      </TableCell>
                    </TableRow>
                  ) : (
                    requisition.items.map((item, idx) => {
                      const input = item.inputId ? inputsMap[item.inputId] : null;
                      const approved = approvedQuantities[idx] ?? 0;
                      const isReduced = approved < item.quantityRequested;
                      const isSeedBatch = isCustomerSeedItem(item);
                      const description = getRequisitionItemDescription(item);
                      return (
                        <TableRow key={idx}>
                          <TableCell>
                            <Badge variant="outline" className={cn("text-xs", isSeedBatch ? "border-amber-300 text-amber-700 bg-amber-50" : "border-blue-200 text-blue-700 bg-blue-50")}>
                              {getRequisitionItemType(item)}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">
                            {item.inputId && !input
                              ? "Loading…"
                              : input?.inputName ?? getRequisitionItemName(item)}
                            {description && (
                              <div className="text-xs font-normal text-muted-foreground">
                                {description}
                              </div>
                            )}
                            <div className="text-xs font-normal text-muted-foreground">
                              Owner: {getRequisitionItemOwner(item)}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {input?.inputCode
                              ? `Reference ${input.inputCode}`
                              : getRequisitionItemReference(item)}
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {item.quantityRequested}
                          </TableCell>
                          <TableCell className="text-right">
                            <TextInput
                              type="number"
                              min="0"
                              max={item.quantityRequested}
                              value={approved}
                              onChange={(e) =>
                                setApprovedQuantities({
                                  ...approvedQuantities,
                                  [idx]: parseFloat(e.target.value) || 0,
                                })
                              }
                              disabled={isPending || showRejectForm}
                              className={cn(
                                "w-24 text-right",
                                isReduced && "border-amber-400 focus-visible:ring-amber-400"
                              )}
                            />
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {item.unitOfMeasure}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Approve notes */}
          {!showRejectForm && (
            <Field>
              <FieldLabel>Approval Notes <span className="text-muted-foreground font-normal">(optional)</span></FieldLabel>
              <Textarea
                placeholder="Add any notes for this approval…"
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                disabled={isPending}
                rows={3}
              />
            </Field>
          )}

          {/* Rejection form */}
          {showRejectForm && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 space-y-3">
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-4 w-4" />
                <p className="text-sm font-medium">Rejecting this requisition</p>
              </div>
              <Field>
                <FieldLabel>
                  Rejection Reason <span className="text-destructive">*</span>
                </FieldLabel>
                <Textarea
                  placeholder="Explain why you are rejecting this requisition…"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  disabled={isPending}
                  rows={4}
                />
              </Field>
            </div>
          )}

          {/* Action footer */}
          <Separator />
          <div className="flex gap-2 justify-between">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            {!showRejectForm ? (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => setShowRejectForm(true)}
                  disabled={isPending}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Reject
                </Button>
                <Button
                  onClick={handleApprove}
                  disabled={isPending || loadingInputs}
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  {approveMutation.isPending ? "Approving…" : "Approve"}
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowRejectForm(false)}
                  disabled={isPending}
                >
                  Back
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleReject}
                  disabled={isPending || !rejectionReason.trim()}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  {rejectMutation.isPending ? "Rejecting…" : "Confirm Rejection"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>

      <ConfirmDialog
        open={pendingAction === "approve"}
        onOpenChange={(o) => !o && setPendingAction(null)}
        title="Approve this requisition?"
        description="The store will be able to issue stock against the approved quantities. The requester will be notified."
        confirmLabel="Approve"
        pending={approveMutation.isPending}
        onConfirm={() => {
          setPendingAction(null);
          performApprove();
        }}
        body={
          requisition && (
            <div className="rounded border bg-muted/30 p-3 space-y-1 text-sm">
              <div>
                <span className="text-muted-foreground">Requisition:</span>{" "}
                <span className="font-medium">{requisition.requisitionNumber ?? requisition.id.slice(0, 8)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Items:</span>{" "}
                {requisition.items.length}
              </div>
            </div>
          )
        }
      />

      <ConfirmDialog
        open={pendingAction === "reject"}
        onOpenChange={(o) => !o && setPendingAction(null)}
        title="Reject this requisition?"
        description="The requester will be notified with the reason. They'll need to raise a new requisition if they still need the items."
        confirmLabel="Reject"
        variant="destructive"
        pending={rejectMutation.isPending}
        onConfirm={() => {
          setPendingAction(null);
          performReject();
        }}
        body={
          <div className="rounded border bg-muted/30 p-3 text-sm">
            <div className="text-muted-foreground mb-1">Reason</div>
            <div className="whitespace-pre-wrap">{rejectionReason}</div>
          </div>
        }
      />
    </Dialog>
  );
};
